import { Box, BoxProps, styled, Typography } from '@mui/material';
import { TodoListItem, TodoListItemProps } from 'redesign';
import { paths } from 'ogp/paths';
import { DashboardTodoResponse } from 'api/services/dashboard/dashboard-service.schema';
import { RouteDefinition } from 'Routes';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { HeaderComponent } from '../shared/components/header-component';

type GeneratePathFn = (
  route: RouteDefinition,
  params?: { [key: string]: string | undefined },
  queryParams?: { [key: string]: string | undefined },
  subroute?: string
) => string;

type TodoSectionProps = {
  data: DashboardTodoResponse;
  generatePath: GeneratePathFn;
  companyId: string | undefined;
};

export const TodoSection = ({ data, generatePath, companyId }: TodoSectionProps) => {
  const mappedData = useMemo(
    () => toDoListMapper({ data, generatePath, companyId }),
    [data, generatePath, companyId]
  );
  const isLoading = Object.keys(data).length === 0;
  const emptyItem = { title: '', subtitle: '', buttonText: '', navigationUrl: '' };
  return (
    <Box>
      <HeaderComponent titleText="Openstaande taken" downloadUrl="#" />
      <StyledListBox>
        {isLoading ? <TodoListItem key="loading" {...emptyItem} /> : null}
        {!isLoading && mappedData.length === 0 ? (
          <StyledEmptyListMessage>
            <Typography variant="body1">Goed bezig! Je hebt alle taken afgerond.</Typography>
          </StyledEmptyListMessage>
        ) : (
          mappedData.map((item) => <TodoListItem key={item.title} {...item} />)
        )}
      </StyledListBox>
    </Box>
  );
};

const StyledListBox = styled(Box)<BoxProps>(({ theme }) => ({
  height: '83%',
  background: theme.palette.lwPrimaryAccentLight['5'],
  borderRadius: '16px',
  marginTop: theme.spacing(4),
  gap: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  paddingTop: theme.spacing(3),
  paddingRight: theme.spacing(5),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
}));

const StyledEmptyListMessage = styled(Box)<BoxProps>(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing(3),
}));

const toDoListMapper = ({ data, generatePath, companyId }: TodoSectionProps) => {
  const response: TodoListItemProps[] = [];

  const currentDate = DateTime.now().toFormat('yyyy-MM-dd');
  const futureDate = DateTime.now().plus({ days: 3 }).toFormat('yyyy-MM-dd');

  if (data?.openCheckouts) {
    response.push({
      title: `${data.openCheckouts} openstaande checkouts`,
      subtitle: 'Je hebt openstaande aanmeldingen',
      buttonText: 'Naar jouw checkouts',
      navigationUrl: generatePath(paths.CheckoutsInbox, { companyId }),
    });
  }
  if (data?.fullFillYourWork && data?.fullFillYourWork.length !== 0) {
    const navigationUrl = generatePath(
      paths.ShiftPlanning,
      { companyId },
      {
        filledStatus: 'inProgress',
        fromDate: currentDate,
        toDate: futureDate,
      },
      'table'
    );
    response.push({
      title: 'Vul jouw openstaande diensten',
      subtitle: 'Jouw dienst begint binnenkort!',
      buttonText: 'Naar jouw planning',
      navigationUrl,
    });
  }
  if (data?.notPlannedWork) {
    response.push({
      title: 'Tijd om vooruit te plannen',
      subtitle: 'Minder dan 14 dagen om te plannen',
      buttonText: 'Naar planning',
      navigationUrl: generatePath(paths.ShiftPlanning, { companyId }, {}, 'table'),
    });
  }
  return response;
};
